/**
 * PKCE (Proof Key for Code Exchange) utility functions
 * Implements RFC 7636 for OAuth 2.0 public clients
 */

/**
 * Generates a cryptographically random code verifier
 * @param length Length of the code verifier (43-128 characters, default 128)
 * @returns Base64URL-encoded code verifier
 */
export function generateCodeVerifier(length = 128): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);

    // Convert to base64url (RFC 7636)
    return base64UrlEncode(array);
}

/**
 * Generates a code challenge from a code verifier using SHA256
 * @param verifier The code verifier
 * @returns Promise resolving to base64URL-encoded code challenge
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Base64URL encoding (RFC 4648 Section 5)
 * Replaces + with -, / with _, and removes padding =
 */
function base64UrlEncode(array: Uint8Array): string {
    // Convert Uint8Array to binary string
    let binary = '';
    for (let i = 0; i < array.length; i++) {
        binary += String.fromCharCode(array[i]);
    }

    // Use btoa to get base64, then convert to base64url
    const base64 = btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
